import { useMemo } from "react"
import { useQuery } from "urql"

import type { ProductTest, ProductTestListFailure, ProductTestListSuccess } from "@/graphql/codegen/graphql"
import { GetExistingProductTestListDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseHasExistingProductTestProps {
  filter: {
    productId: number
    lotNumber: string
    batchNumber: string | null
  }
  productTestId?: string
  orderId?: number | null
}

interface UseHasExistingProductTestReturn {
  hasExisting: boolean
  hasExistingToLinkPo: boolean
  existingProductTest: ProductTest | null
  isFetching: boolean
}

export const useHasExistingProductTest = ({
  filter,
  productTestId,
  orderId,
}: UseHasExistingProductTestProps): UseHasExistingProductTestReturn => {
  const notify = useNotificationsStore(({ enqueueNotification }) => enqueueNotification)
  const [{ data, fetching, error }] = useQuery({
    query: GetExistingProductTestListDocument,
    variables: { input: { filter } },
  })

  const { hasExisting, hasExistingToLinkPo, existingProductTest } = useMemo(() => {
    if (!data) return { hasExisting: false, hasExistingToLinkPo: false, existingProductTest: null }

    if (data.productTest.list.__typename === "ProductTestListFailure" || error) {
      notify({
        message: "Failed to fetch product tests.",
        content: error?.message ?? (data.productTest.list as ProductTestListFailure).error.message,
        type: NotificationType.Error,
      })
      return { hasExisting: false, hasExistingToLinkPo: false, existingProductTest: null }
    }

    const productTests = (data.productTest.list as ProductTestListSuccess).productTests

    // Filter out the current product test if we're editing
    const filteredProductTests = productTestId
      ? productTests.filter((test) => test.productTestId !== productTestId)
      : productTests

    // Check for existing product tests with same lot# and batch#
    const testsWithSameLotBatch = filteredProductTests.filter(
      (test) =>
        test.productId === filter.productId &&
        test.lotNumber === filter.lotNumber &&
        test.batchNumber === filter.batchNumber
    )

    // If no orderId is provided, just check if lot+batch combination exists
    if (!orderId) {
      const foundTest = testsWithSameLotBatch.length > 0 ? testsWithSameLotBatch[0] : null
      return {
        hasExisting: testsWithSameLotBatch.length > 0,
        hasExistingToLinkPo: false,
        existingProductTest: foundTest,
      }
    }

    // Find exact match (lot+batch+PO combination) - this means duplicate exists
    const exactMatch = testsWithSameLotBatch.find(
      (test) => test.orders?.some((order) => order.orderId === orderId) ?? false
    )
    // Find test with lot+batch but without this specific PO - this can be linked
    const testToLink = testsWithSameLotBatch.find((test) => !test.orders?.some((order) => order.orderId === orderId))
    // Return the found productTest - prefer exact match, fallback to test that can be linked
    const foundTest = exactMatch || testToLink || null

    return {
      hasExisting: !!exactMatch,
      hasExistingToLinkPo: !!testToLink,
      existingProductTest: foundTest,
    }
  }, [data, error, filter, productTestId, notify, orderId])

  return {
    hasExisting,
    hasExistingToLinkPo,
    existingProductTest,
    isFetching: fetching,
  }
}
